import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  BookOpen, Ship, Calendar, PoundSterling, FileText, 
  FolderOpen, ShieldCheck, Receipt, ChevronRight, ChevronDown,
  Anchor, ArrowRight, Info, UserPlus, Briefcase
} from 'lucide-react';

const chapters = [
  {
    id: 'getting-started',
    icon: Anchor,
    title: 'Getting Started',
    subtitle: 'What is SED and how this app helps you',
    content: [
      {
        heading: "What is Seafarer's Earnings Deduction (SED)?",
        body: "SED is a UK tax relief that allows qualifying seafarers to deduct 100% of their foreign earnings from UK income tax. If you work on ships or offshore installations outside UK waters, you may be eligible to pay no UK tax on those earnings. This is governed by HMRC's HS205 guidance."
      },
      {
        heading: "Who qualifies?",
        body: "To qualify for SED, you must meet these HMRC criteria:",
        list: [
          "You must have a 365-day 'eligible period' that includes at least one trip abroad",
          "During that period, your UK days must not exceed half the total days (the 'half-day rule')",
          "You must not spend more than 183 consecutive days in the UK (the '183-day rule')",
          "Your duties must be performed wholly or partly outside the UK"
        ]
      },
      {
        heading: "How Seafarer Logbook helps",
        body: "This app tracks every day you spend abroad and in the UK, calculates your qualifying days using HMRC's exact HS205 method, and tells you in real-time whether your SED claim is valid. It also stores your wage slips, generates reports for your accountant, and warns you before your claim is at risk."
      },
      {
        heading: "First steps",
        body: "After creating your account:",
        list: [
          "Select your current tax year using the selector in the top-right corner of any page (UK tax years run 6 April to 5 April)",
          "Add your trips starting with work trips on vessels, then add holidays and leave",
          "Upload your wage slips via Pay & Contracts > Documents so the app can compare calculated vs actual earnings",
          "Check the SED Eligibility page regularly to monitor your claim status"
        ]
      }
    ]
  },
  {
    id: 'trips',
    icon: Ship,
    title: 'Adding & Managing Trips',
    subtitle: 'Recording your time at sea and ashore',
    content: [
      {
        heading: "Trip Types",
        body: "The app supports 9 trip types to capture your full working pattern:",
        list: [
          "At Sea/On Vessel — Your main work trips on ships or offshore installations. These earn qualifying days",
          "Base Work (Abroad) — Shore-based work abroad (e.g., at a foreign office). Counts as qualifying",
          "Base Work (UK) — Shore-based UK work. Tracked but 0 qualifying days",
          "Course (Abroad) — Training courses held outside the UK. Counts as qualifying",
          "Course (UK) — UK-based training. Tracked but 0 qualifying days",
          "Paid Annual Leave — Leave periods. Tracked for earnings but 0 qualifying days",
          "Qualifying Holiday — Foreign holidays that count towards your SED days abroad",
          "Visa Run — Short trips abroad for visa or permit renewals. Counts as qualifying",
          "Wage Adjustment — Ad-hoc additions or deductions to earnings"
        ]
      },
      {
        heading: "Adding a Work Trip (Step-by-Step Wizard)",
        body: "Work trips use a 3-step wizard:",
        list: [
          "Step 1 — Core Details: Select an existing contract or create a new one, choose the trip type, enter vessel name, contractor/employer, ports of joining/leaving, and UK waters status. The waters status has three options: Outside UK Waters (the default, for voyages entirely abroad), Within UK Waters (for vessels that never leave UK territorial waters — 0 qualifying SED days), or Left/Entered UK Waters (for multi-leg voyages that cross in and out)",
          "Step 2 — Dates & Travel: Enter vessel join/leave dates and UK departure/return dates. The wizard asks a series of guided questions about travel and hotel stays before and after the trip, showing only the fields relevant to your situation",
          "Step 3 — Earnings: Add day rates by period (standard, travel day, standby, etc.). If you receive a different rate for travel or hotel days, tick 'Separate rate for travel/hotel days' to enter those rates independently. You can also add custom uplifts (e.g., offshore bonus, hazard pay) with a name, amount, and date range — these stack on top of your standard day rate"
        ]
      },
      {
        heading: "Travel & Hotel Stops",
        body: "For work trips, courses abroad, base work abroad, and visa runs, the wizard asks whether you had travel days or hotel stays before and after the trip:",
        list: [
          "Pre-trip UK stops — Travel or hotel stays in the UK before departing (e.g., hotel near the airport the night before flying out)",
          "Pre-trip Abroad stops — Travel or hotel stays abroad before joining the vessel (e.g., hotel in a foreign port city awaiting crew change)",
          "Post-trip UK stops — Travel or hotel stays in the UK after returning (e.g., overnight hotel before driving home)",
          "Post-trip Abroad stops — Travel or hotel stays abroad after leaving the vessel (e.g., waiting for a connecting flight)",
          "Hotel stops record check-in and check-out dates. Travel stops record the travel date. All stops appear on the trip card with colour-coded badges"
        ]
      },
      {
        heading: "Continuous Trips (Crew Changes at Sea)",
        body: "If you transfer between vessels without returning to the UK, use the continuous trip checkboxes. 'Already abroad' means you didn't leave the UK for this trip (you were already on the previous one). 'Stayed abroad' means you didn't return to the UK after this trip. The app will correctly merge these into one HS205 worksheet entry."
      },
      {
        heading: "Multi-Leg Voyages",
        body: "For ships that enter and exit UK waters multiple times during a single trip (e.g., North Sea operations), tick 'Ship entered/exited UK waters multiple times' and add each crossing period. Only time outside UK waters counts as qualifying."
      },
      {
        heading: "Other Trip Types",
        body: "Non-work trips (qualifying holidays, courses, base work, visa runs) use a simplified form. Enter the relevant details and UK departure/return dates. Qualifying days are calculated automatically. Foreign trip types also support travel and hotel stops."
      },
      {
        heading: "Editing & Deleting Trips",
        body: "Click any trip card to expand it and see full details. Use the edit (pencil) icon to modify or the delete (bin) icon to remove. You can also edit trips directly from the Pay & Contracts page."
      },
      {
        heading: "Tax Year Boundaries",
        body: "If a trip spans April 5th/6th (the tax year boundary), the app automatically splits the qualifying days between tax years. A purple 'Spans Tax Year' badge appears on these trips showing how the days are allocated."
      }
    ]
  },
  {
    id: 'earnings',
    icon: PoundSterling,
    title: 'Pay & Contracts',
    subtitle: 'Tracking and reconciling your pay',
    content: [
      {
        heading: "How it works",
        body: "The Pay & Contracts page has four tabs: Earnings, Contracts, Expenses, and Documents. The Earnings tab breaks down your earnings by month based on your trip day rates and contract terms. Earnings reflect your actual pay received for all calendar days worked. The HMRC midnight rule (which excludes the return day) is applied separately on the SED Eligibility page for qualifying days only. Each month expands into a detailed view."
      },
      {
        heading: "Contracts Tab",
        body: "Contracts are the source of truth for your employment rates. The Contracts tab shows all your contracts with employer, rates, status, and for salaried contracts a progress bar tracking days worked against your threshold. Add, edit, or delete contracts here — or add them directly from the Trip Wizard. The app supports two pay structures:",
        list: [
          "Day Rate — A fixed daily rate from an employer (and optionally via a client/agency)",
          "Salaried — An annual salary plus a daily uplift for each day worked at sea. The app auto-calculates your monthly base (annual salary / 12) and post-contract uplift rate"
        ]
      },
      {
        heading: "Post-Contract Uplift",
        body: "Many salaried seafarers work beyond their contracted days (e.g. 120 days/year). When that happens, the daily uplift automatically switches from your standard rate to the higher post-contract uplift. The Earnings tab shows a '(post-contract)' indicator and the Contracts tab shows a progress bar with 'Contract fulfilled' once the threshold is exceeded."
      },
      {
        heading: "Overview Tab",
        body: "Within each month card, shows your gross calculated earnings (from trip day rates or contract terms), the actual gross earnings from wage slips (entered manually or via AI extraction), and the difference between them. Both figures use gross pay — if you see a discrepancy, it's usually because trip details (rates, uplifts, dates) differ from your actual contract terms, not because of tax deductions. Use the 'Add' button to enter how much you actually received in GBP — this is essential for tax reporting when you're paid in foreign currencies."
      },
      {
        heading: "Trips Tab",
        body: "Lists all trips that overlap with the selected month, showing the vessel/location, dates, earnings, and day rates. For salaried trips that have exceeded contract days, the rate displays with a '(post-contract)' indicator. Use the edit icon to jump straight to the trip wizard, or the view icon to navigate to the Trips page."
      },
      {
        heading: "Documents Tab",
        body: "Manage wage slips and other documents attached to each month. Upload PDFs, preview them, and use AI extraction to pull earnings data automatically. Documents that have been linked to a trip will show the vessel name alongside — helping you cross-reference."
      },
      {
        heading: "Expenses Tab",
        body: "Track allowable expenses by category. Add expenses month-by-month so they appear alongside your earnings for a complete financial picture."
      },
      {
        heading: "Earnings Chart",
        body: "The bar chart at the top visualises your monthly earnings across the tax year, helping you spot patterns and ensure nothing is missing."
      }
    ]
  },
  {
    id: 'documents',
    icon: FolderOpen,
    title: 'Documents & AI Extraction',
    subtitle: 'Uploading wage slips and extracting data (Pay & Contracts > Documents tab)',
    content: [
      {
        heading: "Uploading Documents",
        body: "Upload wage slips, P60s, flight stubs, vessel midnight positions, and other supporting documents. Each document is tagged with a type, tax year, and month so it appears in the right place on your Pay & Contracts page."
      },
      {
        heading: "Document Types",
        body: "Supported categories include:",
        list: [
          "Payslips — Wage slips",
          "P45/P60/P11D — End of employment certificates, end of year tax summaries, and benefits & expenses declarations (shown in separate sections)",
          "Travel — Flight tickets, vessel midnight positions, seaman's discharge book",
          "Receipts — Expense receipts and invoices",
          "Other — Any additional supporting documentation"
        ]
      },
      {
        heading: "AI-Powered Wage Slip Extraction",
        body: "After uploading a wage slip PDF, click 'Extract Data' to use AI to automatically read the gross earnings, net pay, tax, employer name, vessel, and pay period. The extracted data is then compared against your calculated trip earnings to highlight any discrepancies."
      },
      {
        heading: "Password-Protected PDFs",
        body: "Some employers issue password-protected wage slips. When extracting, you'll be prompted to enter the PDF password. The app decrypts the file temporarily for AI reading — the password is not stored."
      },
      {
        heading: "Grid & List Views",
        body: "Toggle between grid view (visual cards) and list view (compact table) depending on how many documents you have. Use the search bar and filters to find specific documents quickly."
      },
      {
        heading: "Linking Documents to Trips",
        body: "You can link any uploaded document (flight stubs, boarding passes, wage slips) to a specific trip for easy cross-referencing. There are two ways to link:",
        list: [
          "From the Documents page — Click the 'Link' button on any document card. A trip picker opens where you can search and select the relevant trip",
          "From the Trips page — Expand a trip card and click 'Attach' in the Documents section. A document picker shows all your available files",
          "Each document can be linked to one trip at a time. Linked documents appear in the expanded trip card and in the Pay & Contracts Documents tab with the vessel name shown",
          "To unlink, click the Link button again and choose 'Unlink', or hover over the document in the trip card and click the unlink icon"
        ]
      }
    ]
  },
  {
    id: 'eligibility',
    icon: ShieldCheck,
    title: 'SED Eligibility (HS205)',
    subtitle: "HMRC's exact calculation method",
    content: [
      {
        heading: "Status Cards",
        body: "Four cards at the top summarise your current eligible period:",
        list: [
          "SED Claim Status — Whether you're Eligible, Building, or have a Broken claim",
          "365-Day Target — Progress towards the minimum 365-day eligible period",
          "Half-Day Rule — Your 'days in hand' buffer. Shows UK days vs the maximum allowed (half your total days). The progress bar stays cyan when healthy, amber when cautious, red when breached",
          "Claim Breaks On — The projected date your claim would break if you stay continuously in the UK. Based on whichever comes first: the half-day rule or the 183-consecutive-day limit"
        ]
      },
      {
        heading: "Tax Year Claim Breakdown",
        body: "Shows how your eligible period maps to each tax year. The first tax year where your eligible period started is labelled 'Partial' (since it didn't begin on April 6th). Subsequent full tax years are labelled 'Full Year'. Each row shows the date range covered, days, and qualifying earnings for that tax year."
      },
      {
        heading: "The HS205 Working Sheet",
        body: "This page replicates HMRC's official HS205 calculation worksheet. It shows every UK departure and return, the running total of days, and whether the half-day rule is breached at each point. This is the same calculation your accountant would perform."
      },
      {
        heading: "Understanding the Columns",
        body: "The worksheet has 8 columns:",
        list: [
          "Column A — Date left UK",
          "Column B — Date returned to UK",
          "Column C — Days abroad (B minus A)",
          "Column D — Days in UK since last return",
          "Column E — Running total of all days (abroad + UK)",
          "Column F — Running total of UK days only",
          "Column G — Half of Column E (your threshold)",
          "Column H — YES if Column F exceeds Column G (breach)"
        ]
      },
      {
        heading: "365-Day Eligible Period",
        body: "Your eligible period starts from your first UK departure and grows with each trip. Once Column E reaches 365, you've completed an eligible period. As long as Column H shows 'No' (meaning your UK days haven't exceeded half the total), your SED claim is valid for earnings within that period."
      },
      {
        heading: "How You Travelled Doesn't Matter",
        body: "The HS205 worksheet tracks the date you left the UK regardless of how you travelled. Whether you flew from an airport or sailed from a UK port, the departure date counts the same way for SED purposes."
      },
      {
        heading: "What Breaks a Claim",
        body: "Two things can break your SED claim:",
        list: [
          "Half-day rule breach — Your UK days exceed half your total days (Column F > Column G). The table restarts as a new eligible period",
          "183-day rule — You spend more than 183 consecutive days in the UK between trips. This splits the table into separate segments"
        ]
      },
      {
        heading: "Export for Accountant",
        body: "Click 'Export CSV' to download the HS205 worksheet in spreadsheet format. Your accountant can use this directly when filing your tax return."
      }
    ]
  },
  {
    id: 'expenses',
    icon: Receipt,
    title: 'Expense Tracking',
    subtitle: 'Logging work-related costs (Pay & Contracts > Expenses tab)',
    content: [
      {
        heading: "Why track expenses?",
        body: "Work-related expenses that aren't reimbursed by your employer may be tax-deductible. Keeping a clear record helps when filing your tax return or if HMRC requests evidence."
      },
      {
        heading: "Adding Expenses",
        body: "Log each expense with a date, category, description, and amount. Mark whether it's tax-deductible and track the reimbursement status (Not Claimed, Pending, Reimbursed). You can optionally link an expense to a specific trip or uploaded receipt."
      },
      {
        heading: "Categories",
        body: "Expenses are grouped into 9 categories: Travel, Accommodation, Equipment, Training, Medical, Visa & Permits, Communication, Subsistence, and Other."
      },
      {
        heading: "Summary Cards",
        body: "The top of the page shows totals for all expenses, tax-deductible expenses, reimbursed amounts, and pending claims — helping you see your net out-of-pocket costs at a glance."
      }
    ]
  },
  {
    id: 'reports',
    icon: FileText,
    title: 'End of Year Reports',
    subtitle: 'Generating documents for tax returns',
    content: [
      {
        heading: "PDF Tax Summary",
        body: "Generate a comprehensive PDF report for any tax year. It includes your taxpayer information, SED claim status, complete trip history with qualifying days and earnings, earnings by contractor, tax deductions from wage slips, expenses summary, and supporting documents list."
      },
      {
        heading: "HMRC Report Pack",
        body: "Download an HMRC-ready ZIP package formatted for Self Assessment submission. Includes a cover letter with claim summary, the full HS205 working sheet in both PDF and CSV format, a complete trip schedule, and a wage slip summary with tax deductions."
      },
      {
        heading: "Excel Workbook",
        body: "Export a formatted multi-sheet Excel workbook containing: Summary (key stats and SED status), Trips (full trip schedule), Monthly Earnings (breakdown by month), Expenses (by category), and the HS205 Worksheet. Perfect for accountants who prefer spreadsheets."
      },
      {
        heading: "ZIP Tax Package",
        body: "Download a complete package containing your PDF report, trips CSV, expenses CSV, and all uploaded supporting documents. This gives your accountant everything in one file."
      },
      {
        heading: "Share with Accountant",
        body: "Email your tax report directly to your accountant from within the app. Enter their email, add an optional personal message, and choose whether to include the full ZIP package with CSVs and supporting documents. The email arrives with a professional Seafarer Logbook template and the report attached."
      },
      {
        heading: "Print-Friendly Report",
        body: "Click 'Print View' to open a clean, white-background version optimised for printing. This opens in a new tab with the browser's print dialog."
      },
      {
        heading: "HS205 CSV Export",
        body: "From the SED Eligibility page, export the HS205 worksheet as a CSV. This mirrors HMRC's official format and can be attached to your Self Assessment."
      }
    ]
  },
  {
    id: 'invite',
    icon: UserPlus,
    title: 'Invite a Friend',
    subtitle: 'Earn free subscription weeks through referrals',
    content: [
      {
        heading: "How the Referral Programme Works",
        body: "You can invite fellow seafarers to join the app. When they accept your invitation and create an account, you both receive 7 days (1 week) of free subscription. You can send up to 12 successful invitations per rolling 12-month period."
      },
      {
        heading: "24-Hour Invitation Window",
        body: "All invitations expire after 24 hours. If the invitee doesn't create an account within that time, the invitation is automatically cancelled. This ensures your invitations remain valuable:",
        list: [
          "Expired invitations do NOT count against your annual limit of 12",
          "You can re-send an invitation to the same person after it expires",
          "Only accepted invitations earn rewards and count towards your limit"
        ]
      },
      {
        heading: "Sending an Invitation",
        body: "Go to the 'Invite a Friend' page in the sidebar. Enter the person's email address and click 'Send Invite'. They'll receive an email with a signup link that's valid for 24 hours. Your referral code is automatically included."
      },
      {
        heading: "How Rewards Are Applied",
        body: "Free weeks earned through referrals accumulate and are applied at the end of your billing year. For example:",
        list: [
          "6 successful invitations = 6 weeks free at year-end (approximately 1.5 months)",
          "12 successful invitations = 12 weeks free at year-end (approximately 3 months)",
          "With a standard subscription of £3.99/month, 12 referrals saves you around £11"
        ]
      }
    ]
  },
  {
    id: 'accountant',
    icon: Briefcase,
    title: 'My Accountant',
    subtitle: 'Linking, messaging, and sharing documents securely',
    content: [
      {
        heading: "Connecting to Your Accountant",
        body: "The My Accountant tab lets you securely connect with your tax accountant. Once linked, they can view your SED data, trip history, documents, and earnings to prepare your tax returns — without you needing to send files manually."
      },
      {
        heading: "Inviting Your Accountant",
        body: "If your accountant isn't already on Seafarer Logbook, you can invite them:",
        list: [
          "Go to My Accountant > Overview and enter their email address",
          "They'll receive a secure invitation email with a unique link code",
          "Each invitation generates a fresh, unique code that expires after 48 hours",
          "Once they register and enter the code on their side, you'll receive a confirmation request"
        ]
      },
      {
        heading: "Sharing a Link Code Directly",
        body: "If you prefer, you can generate a link code and share it with your accountant by phone or in person. Each time you click 'Generate Link Code', a new unique code is created. Codes expire after 48 hours for security."
      },
      {
        heading: "Confirming the Link",
        body: "When your accountant redeems your code, you'll see a confirmation request with their GDPR consent statement. Review the consent and click 'Confirm & Grant Access' to establish the link. You can revoke access at any time."
      },
      {
        heading: "Messaging Your Accountant",
        body: "Once linked, the Messages tab provides a direct, secure chat with your accountant. You can discuss tax queries, flag missing documents, or coordinate on your SED claim. You can also attach files directly to messages."
      },
      {
        heading: "Uploading Documents for Your Accountant",
        body: "The Documents tab lets you upload missing paperwork and share it with your accountant in one step:",
        list: [
          "Select a document type (Wage Slip, Boarding Pass, P60, P45, Contract, Receipt, or Other)",
          "The app auto-detects which trip the document belongs to based on dates in the filename",
          "You can manually override the trip selection using the dropdown",
          "Uploaded documents appear on the relevant trip card and in your Documents section",
          "Your accountant is automatically notified when you upload a document"
        ]
      },
      {
        heading: "Revoking Access",
        body: "You can revoke your accountant's access at any time from the Overview tab. They will immediately lose access to your SED data, trips, and documents. Your data is never deleted — only the access link is removed."
      }
    ]
  },
  {
    id: 'tips',
    icon: Info,
    title: 'Tips & Best Practices',
    subtitle: 'Getting the most from Seafarer Logbook',
    content: [
      {
        heading: "Keep trips up to date",
        body: "Enter trips as soon as you return from each assignment. This keeps your qualifying days and earnings accurate and gives you early warning if your claim is at risk."
      },
      {
        heading: "Use the correct dates",
        body: "HMRC counts the day you leave the UK as a qualifying day (you're outside the UK at midnight) but NOT the day you return (you're in the UK at midnight). The app applies this rule automatically, but make sure your departure and return dates are accurate."
      },
      {
        heading: "Watch the 183-day rule",
        body: "The SED Eligibility page shows the 'Claim Breaks On' date — the projected date your claim would break if you stay in the UK. If that date is approaching, plan a trip abroad. Even a short trip resets the consecutive-days counter."
      },
      {
        heading: "Record qualifying holidays",
        body: "Foreign holidays count as qualifying days. Every holiday abroad helps maintain your SED eligibility. Make sure to log them — even short weekend trips."
      },
      {
        heading: "Plan ahead with future trips",
        body: "You can add trips with future dates to see how they'll affect your SED eligibility. Future trips appear with a 'Future' badge and are included in the live calculation so you can plan your qualifying days ahead of time."
      },
      {
        heading: "Log travel stops for accuracy",
        body: "When adding a work trip, answer the pre-trip and post-trip travel questions. Hotel stays abroad before or after a trip extend your qualifying period. The more accurate your travel stops, the more precise your HS205 calculation."
      },
      {
        heading: "Upload wage slips monthly",
        body: "Uploading wage slips as they arrive makes year-end reconciliation much easier. The AI extraction saves you from manually entering figures. Use the bulk upload feature to process multiple months at once."
      },
      {
        heading: "Invite friends to earn free weeks",
        body: "Each successful referral earns you and your friend a free week of subscription. With 12 successful invitations per year, you could save up to 3 months of subscription costs. Remember: invitations expire after 24 hours, so let your friends know to sign up promptly."
      },
      {
        heading: "Review before tax deadline",
        body: "Before your Self Assessment deadline (31 January), use the End of Year Reports page to generate your full tax package. Cross-check the HS205 worksheet with your accountant."
      },
      {
        heading: "Tax year runs April to April",
        body: "The UK tax year runs from 6 April to 5 April. Make sure you're looking at the right year when checking your SED status. Use the tax year selector in the top-right corner of any page to switch between years."
      }
    ]
  }
];

const ChapterSection = ({ section }) => (
  <div className="space-y-2">
    <h3 className="text-lg font-semibold text-white">{section.heading}</h3>
    {section.body && <p className="text-slate-300 leading-relaxed">{section.body}</p>}
    {section.list && (
      <ul className="space-y-1.5 ml-1 mt-2">
        {section.list.map((item, i) => (
          <li key={`list-${i}`} className="flex gap-2 text-slate-300">
            <ArrowRight className="h-4 w-4 text-amber-400 mt-1 shrink-0" />
            <span className="leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const Guide = () => {
  const [openChapter, setOpenChapter] = useState('getting-started');

  const toggleChapter = (id) => {
    setOpenChapter(openChapter === id ? null : id);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
          <BookOpen className="h-7 w-7 text-amber-400" />
          How to Use Seafarer Logbook
        </h1>
        <p className="text-slate-400 mt-2">
          A comprehensive guide to tracking your Seafarer's Earnings Deduction
        </p>
      </div>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="py-4">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-medium">Chapters</p>
          <div className="flex flex-wrap gap-2">
            {chapters.map((ch) => (
              <button
                key={ch.id}
                onClick={() => {
                  setOpenChapter(ch.id);
                  setTimeout(() => {
                    document.getElementById(ch.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 50);
                }}
                data-testid={`guide-nav-${ch.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all ${
                  openChapter === ch.id
                    ? 'bg-amber-600 text-white'
                    : 'bg-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                <ch.icon className="h-3.5 w-3.5" />
                {ch.title}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {chapters.map((chapter) => {
          const isOpen = openChapter === chapter.id;
          return (
            <Card
              key={chapter.id}
              id={chapter.id}
              className={`border transition-all duration-200 ${
                isOpen ? 'bg-slate-800 border-amber-600/50' : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
              }`}
            >
              <button
                onClick={() => toggleChapter(chapter.id)}
                data-testid={`guide-chapter-${chapter.id}`}
                className="w-full text-left"
              >
                <CardHeader className="py-4 px-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isOpen ? 'bg-amber-600' : 'bg-slate-700'}`}>
                        <chapter.icon className={`h-5 w-5 ${isOpen ? 'text-white' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <CardTitle className="text-base text-white">{chapter.title}</CardTitle>
                        <p className="text-sm text-slate-400 mt-0.5">{chapter.subtitle}</p>
                      </div>
                    </div>
                    {isOpen ? (
                      <ChevronDown className="h-5 w-5 text-amber-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slate-500" />
                    )}
                  </div>
                </CardHeader>
              </button>
              {isOpen && (
                <CardContent className="px-5 pb-5 pt-0 space-y-5 border-t border-slate-700/50 mt-0">
                  <div className="pt-4 space-y-6">
                    {chapter.content.map((section, idx) => (
                      <ChapterSection key={section.heading || `section-${idx}`} section={section} />
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      <Card className="bg-slate-800/30 border-slate-700/50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-slate-300">
                <span className="font-semibold text-amber-400">Disclaimer:</span> Seafarer Logbook is for informational purposes only and is not a substitute for professional tax advice. 
                Always consult a qualified accountant or HMRC directly for official guidance on your Seafarer's Earnings Deduction claim.
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Reference: HMRC HS205 — Seafarer's Earnings Deduction
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Guide;
